// components/parametres/GestionUtilisateurs.jsx
import React, { useState, useEffect } from 'react';
import { 
  Users, Shield, UserPlus, Edit2, Trash2, Key, CheckCircle, 
  XCircle, User, Mail, Crown, AlertTriangle, MoreVertical,
  Circle, Eye
} from 'lucide-react';
import api from '../services/api';
import ModuleRightsModal from './ModuleRightsModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { Toast } from '../components/ui/Toast';
import UserFormModalComplete from './UserFormModalComplete';
import UserDetailModal from './UserDetailModal';
import { useLoader } from '../contexts/LoaderContext';

const GestionUtilisateurs = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRightsModal, setShowRightsModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [quota, setQuota] = useState({ current: 0, max: 2, available: 0, canCreate: false });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      showLoader();
      setLoading(true);
      setError(null);
      const [usersData, quotaData] = await Promise.all([
        api.users.getAll(),
        api.users.getQuota()
      ]);
      if (usersData.success) setUsers(usersData.users);
      if (quotaData.success) setQuota(quotaData.quota);
    } catch (err) {
      setError('Impossible de charger les utilisateurs');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleOpenRightsModal = (user) => { setSelectedUser(user); setShowRightsModal(true); };
  const handleCloseRightsModal = () => { setSelectedUser(null); setShowRightsModal(false); };
  const handleRightsSuccess = () => { handleCloseRightsModal(); showToast('Droits d\'accès mis à jour', 'success'); loadUsers(); };

  const handleOpenCreateModal = () => {
    if (!quota.canCreate) { showToast(`Limite atteinte (${quota.max} maximum)`, 'error'); return; }
    setShowCreateModal(true);
  };

  const handleCreateUser = async (userData) => {
    try {
      const response = await api.users.create(userData);
      const userId = response.user?.id || response.id;
      if (userData.photo && userId) {
        const photoFormData = new FormData();
        photoFormData.append('photo', userData.photo);
        await api.users.uploadPhoto(userId, photoFormData);
      }
      showToast('Utilisateur créé avec succès', 'success');
      setShowCreateModal(false);
      await loadUsers();
    } catch (err) {
      if (err.message.includes('limite') || err.message.includes('USER_LIMIT_REACHED')) {
        showToast(`Limite atteinte (${quota.max} maximum)`, 'error');
      } else {
        showToast(err.message || 'Erreur lors de la création', 'error');
      }
      throw err;
    }
  };

  const handleOpenEditModal = (user) => { setEditingUser(user); setShowEditModal(true); };
  const handleCloseEditModal = () => { setEditingUser(null); setShowEditModal(false); };

  const handleUpdateUser = async (userData) => {
    try {
      await api.users.update(editingUser.id, userData);
      if (userData.photo) {
        const photoFormData = new FormData();
        photoFormData.append('photo', userData.photo);
        await api.users.uploadPhoto(editingUser.id, photoFormData);
      }
      showToast('Utilisateur modifié avec succès', 'success');
      setShowEditModal(false);
      setEditingUser(null);
      await loadUsers();
    } catch (err) {
      showToast(err.message || 'Erreur lors de la modification', 'error');
      throw err;
    }
  };

  const handleResetPassword = async (userId, tempPassword) => {
    try {
      await api.users.resetPassword(userId, tempPassword);
      showToast('Mot de passe réinitialisé', 'success');
    } catch (err) {
      showToast(err.message || 'Erreur réinitialisation', 'error');
    }
  };

  const handleOpenDetailModal = (user) => { setSelectedUserDetail(user); setShowDetailModal(true); };
  const handleCloseDetailModal = () => { setSelectedUserDetail(null); setShowDetailModal(false); };

  const handleDeleteUser = async () => {
    try {
      await api.users.delete(deletingUser.id);
      showToast('Utilisateur supprimé', 'success');
      setDeletingUser(null);
      setDeleteError(null);
      await loadUsers();
    } catch (err) {
      if (err.message.includes('administrateur principal') || err.message.includes('CANNOT_DELETE_PRIMARY_ADMIN')) {
        setDeleteError({ user: deletingUser, message: err.message });
      } else {
        showToast(err.message || 'Erreur suppression', 'error');
      }
      setDeletingUser(null);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Barre de quota
  const quotaPercent = quota.max > 0 ? (quota.current / quota.max) * 100 : 0;
  const quotaColor = quotaPercent >= 100 ? '#ef4444' : quotaPercent >= 80 ? '#f59e0b' : '#448696';

  return (
    <div className="space-y-6">

      {/* ── STATS CARDS ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Quota */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Comptes utilisateurs</p>
              <div className="flex items-baseline gap-1.5">
                <span className="text-3xl font-bold text-gray-900">{quota.current}</span>
                <span className="text-base font-semibold text-gray-400">/ {quota.max}</span>
              </div>
            </div>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
              <Users size={20} className="text-white" />
            </div>
          </div>
          {/* Barre progression */}
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1.5">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(quotaPercent, 100)}%`, backgroundColor: quotaColor }}
            />
          </div>
          <p className="text-xs font-medium" style={{ color: quota.canCreate ? '#22c55e' : '#ef4444' }}>
            {quota.available} utilisateur{quota.available > 1 ? 's' : ''} disponible{quota.available > 1 ? 's' : ''}
          </p>
        </div>

        {/* Admins */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Administrateurs</p>
              <span className="text-3xl font-bold text-gray-900">
                {users.filter(u => u.role === 'admin').length}
              </span>
            </div>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#fed774' }}>
              <Shield size={20} className="text-gray-900" />
            </div>
          </div>
        </div>

        {/* Actifs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Comptes actifs</p>
              <span className="text-3xl font-bold text-gray-900">
                {users.filter(u => u.status === 'active').length}
              </span>
            </div>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#fdac84' }}>
              <CheckCircle size={20} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* ── ERREUR ADMIN PRINCIPAL ── */}
      {deleteError && (
        <div className="bg-white border-2 rounded-2xl overflow-hidden" style={{ borderColor: '#fdac84' }}>
          <div className="px-6 py-5" style={{ backgroundColor: '#fdac8410' }}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#fdac84' }}>
                <AlertTriangle size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-gray-900 mb-1">Impossible de supprimer l'administrateur principal</p>
                <p className="text-xs text-gray-600 leading-relaxed mb-3">
                  Ce compte est l'administrateur principal de votre entreprise. Pour des raisons de sécurité, il ne peut pas être supprimé. Si le responsable a changé, modifiez simplement les informations du compte.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setDeleteError(null); handleOpenEditModal(deleteError.user); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-xs font-semibold transition-all"
                    style={{ backgroundColor: '#448696' }}
                  >
                    <Edit2 size={13} /> Modifier les informations
                  </button>
                  <button
                    onClick={() => setDeleteError(null)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TABLEAU ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* Header tableau */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-sm font-bold text-gray-900">Comptes utilisateurs</h2>
            <p className="text-xs text-gray-400 mt-0.5">{users.length} compte{users.length > 1 ? 's' : ''} configuré{users.length > 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={handleOpenCreateModal}
            disabled={!quota.canCreate}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-xs font-semibold transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: quota.canCreate ? '#448696' : '#cbd5e0' }}
            title={quota.canCreate ? '' : `Limite atteinte (${quota.max} max)`}
          >
            <UserPlus size={15} />
            Nouvel utilisateur
          </button>
        </div>

        {/* En-têtes colonnes */}
        {!loading && !error && users.length > 0 && (
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-100">
            <div className="col-span-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Utilisateur</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Rôle</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Statut</p>
            </div>
            <div className="col-span-4 text-right">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Actions</p>
            </div>
          </div>
        )}

        {/* Contenu */}
        {loading ? null : error ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-red-50">
              <XCircle size={28} className="text-red-400" />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">Erreur de chargement</p>
            <p className="text-xs text-gray-400 mb-4">{error}</p>
            <button
              onClick={loadUsers}
              className="px-4 py-2 rounded-xl text-white text-xs font-semibold"
              style={{ backgroundColor: '#448696' }}
            >
              Réessayer
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: '#44869615' }}>
              <Users size={28} style={{ color: '#448696' }} />
            </div>
            <p className="text-sm font-semibold text-gray-700 mb-1">Aucun utilisateur</p>
            <p className="text-xs text-gray-400">Créez votre premier compte utilisateur</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {users.map((user, index) => (
              <div
                key={user.id}
                className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-gray-50/50 transition-colors"
                style={{ animationDelay: `${index * 40}ms` }}
              >
                {/* Utilisateur */}
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: user.role === 'admin' ? '#fed774' : '#448696' }}
                  >
                    {user.is_primary_admin
                      ? <Crown size={16} className="text-gray-900" />
                      : <User size={16} className={user.role === 'admin' ? 'text-gray-900' : 'text-white'} />
                    }
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  </div>
                </div>

                {/* Rôle */}
                <div className="col-span-2">
                  {user.role === 'admin' ? (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold"
                      style={{ backgroundColor: '#fed77420', color: '#b08d1a' }}
                    >
                      <Crown size={11} />
                      Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600">
                      <User size={11} />
                      Utilisateur
                    </span>
                  )}
                </div>

                {/* Statut */}
                <div className="col-span-2">
                  {user.status === 'active' ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-green-50 text-green-700">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                      Actif
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-400" />
                      Inactif
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="col-span-4 flex items-center justify-end gap-2">
                  {/* Droits d'accès */}
                  <button
                    onClick={() => handleOpenRightsModal(user)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all border hover:shadow-sm"
                    style={{ color: '#b08d1a', borderColor: '#fed77440', backgroundColor: '#fed77415' }}
                    title="Gérer les droits d'accès"
                  >
                    <Key size={13} />
                    Droits
                  </button>

                  {/* Voir détails */}
                  <button
                    onClick={() => handleOpenDetailModal(user)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                    title="Voir les détails"
                  >
                    <Eye size={14} className="text-gray-500" />
                  </button>

                  {/* Éditer */}
                  <button
                    onClick={() => handleOpenEditModal(user)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                    title="Modifier"
                  >
                    <Edit2 size={14} className="text-gray-500" />
                  </button>

                  {/* Supprimer */}
                  {!user.is_primary_admin ? (
                    <button
                      onClick={() => setDeletingUser(user)}
                      className="w-8 h-8 rounded-lg flex items-center justify-center border border-red-100 bg-white hover:bg-red-50 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  ) : (
                    <div className="w-8 h-8" /> /* placeholder pour alignement */
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── MODALS ── */}
      {showDetailModal && selectedUserDetail && (
        <UserDetailModal
          user={selectedUserDetail}
          isOpen={showDetailModal}
          onClose={handleCloseDetailModal}
          onShowToast={showToast}
        />
      )}

      {showCreateModal && (
        <UserFormModalComplete
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
        />
      )}

      {showEditModal && editingUser && (
        <UserFormModalComplete
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onSubmit={handleUpdateUser}
          onResetPassword={handleResetPassword}
          user={editingUser}
        />
      )}

      {showRightsModal && selectedUser && (
        <ModuleRightsModal
          user={selectedUser}
          onClose={handleCloseRightsModal}
          onSuccess={handleRightsSuccess}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingUser}
        onClose={() => setDeletingUser(null)}
        onConfirm={handleDeleteUser}
        title="Supprimer l'utilisateur ?"
        description={`Êtes-vous sûr de vouloir supprimer "${deletingUser?.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}
    </div>
  );
};

export default GestionUtilisateurs;